<template>
  <div>
    <div v-if="!hasReadableCompetitors" class="p-box">
      <div class="p-box__body">
        <GuideToSetUpCompetitor
          :is-more-than-editor="showMoreThanEditorAuthority"
        />
      </div>
    </div>
    <div v-else class="p-box p-box--competitors">
      <!-- start: 写真拡大モーダル -->
      <Modal
        v-if="photoModal"
        option="photo"
        @close="closePhotoModal"
        @prev="prevPhoto"
        @next="nextPhoto"
      >
        <div
          v-for="(photo, i) in photos.data"
          :key="photo.id"
        >
          <div
            v-if="photoActive === i"
            :style="{ 'background-image': `url(${ photo.attributes.image_url })` }"
            class="image"
          >
            <div class="p-photos__modal_info">
              <div class="p-photos__modal_info__store_name">
                {{ photo.attributes.store_name }}
              </div>
              <div class="p-photos__site">
                <div class="p-photos__site__icon p-photos__site__icon__modal">
                  <KkSiteIcon :name="photo.attributes.site_type" />
                </div>
                <div class="p-photos__site__content">
                  <div
                    class="p-photos__site__name"
                    v-text="photo.attributes.site_name"
                  />
                  <div
                    class="p-photos__site__postedAt"
                    v-text="photo.attributes.posted_at"
                  />
                </div>
              </div>
              <div
                v-show="photo.attributes.image_page_link_url !== ''"
                class="p-photos__modal_info__link_wrapper"
              >
                <a :href="photo.attributes.image_page_link_url" class="p-photos__modal_info__link" target="_blank">
                  {{ imageUrlText(photo.attributes.site_type, photo.attributes.site_name) }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <!-- end: 写真拡大モーダル -->

      <!-- start: 絞り込み条件モーダル -->
      <KkFilterModal
        :is-visible="modal"
        :filter-items="filterItems"
        :form-values="formValues"
        :total-count="filteredCount"
        @close="modal = false"
        @submit="handleResetPageAndSubmit"
        @reset="reset"
        @change-check-box="handleChangeFilterModalCheckBox"
      />
      <!-- end: 絞り込み条件モーダル -->

      <!-- start: 絞り込み条件選択 -->
      <form class="p-box__header">
        <div class="p-filtersA">
          <div class="p-filtersA__left">
            <div class="c-buttonFilter" @click="openModal">
              {{ $t('pages.CompaniesCompetitorsPhotos.filters') }}
              <div class="c-buttonFilter__icon">
                <Icon name="iconFilter" />
              </div>
            </div>
          </div>
          <div class="p-filtersA__right">
            <div class="c-buttonSort">
              <button
                class="c-buttonSort__item"
                :class="`is-${sortKey === 'posted_at' && sortOrder}`"
                type="button"
                :disabled="photosLoading"
                @click="sort('posted_at')"
                v-text="$t('pages.CompaniesCompetitorsPhotos.postedOrder')"
              />
            </div>
            <DatePicker
              v-if="period"
              :initial-dates="period"
              opens="left"
              @input="changePeriod"
            />
          </div>
        </div>
      </form>
      <!-- end: 絞り込み条件選択 -->

      <div class="p-box__body">
        <Loading v-if="loading" />
        <template v-else>
          <template v-if="photos">
            <!-- start: 写真一覧 -->
            <div v-if="photos.data.length" class="p-photos">
              <div class="p-photos__total_count_competitor">
                {{ $t('pages.CompaniesCompetitorsPhotos.totalCount', { number: numberLocaleString(photos.meta.total_count) }) }}
              </div>
              <div class="p-photos__items">
                <div
                  v-for="(photo, i) in photos.data"
                  :key="i"
                  class="p-photos__item"
                >
                  <div
                    class="p-photos__image"
                    @click="openPhotoModal(i)"
                  >
                    <div :style="{ 'background-image': `url(${ photo.attributes.image_url })` }" />
                  </div>
                  <div class="p-photos__summary">
                    <div class="p-photos__storeName p-photos__color_white">
                      <div class="p-photos__storeName__tag">
                        {{ $t('pages.CompaniesCompetitorsPhotos.competitors') }}
                      </div>
                      {{ photo.attributes.competitor_name }}
                    </div>
                    <div class="p-photos__site">
                      <div class="p-photos__site__icon">
                        <KkSiteIcon :name="photo.attributes.site_type" />
                      </div>
                      <div class="p-photos__site__content">
                        <div
                          class="p-photos__site__name p-photos__color_white"
                          v-text="photo.attributes.site_name"
                        />
                        <div
                          class="p-photos__site__postedAt p-photos__color_white"
                          v-text="photo.attributes.posted_at"
                        />
                      </div>
                    </div>
                    <div class="p-photos__note">
                      {{ $t('pages.CompaniesCompetitorsPhotos.myself') }}：<span>{{ photo.attributes.store_name }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- end: 写真一覧 -->

            <!-- start: no data -->
            <div v-else class="noData" v-text="$t('messages.noData')" />
            <!-- start: no data -->

            <!-- start: ページネーション -->
            <div v-if="photos.meta.total_pages" class="p-controller">
              <div class="p-controller__count p-controller__color_white">
                <p class="p-controller__count__name-for-competitor">
                  {{ $t('labels.perPageCount') }}
                </p>
                <div class="p-controller__count__select">
                  <div class="p-controller__count__select__competitor">
                    <select :value="displayNumber" @change="changeDisplayNumber($event)">
                      <option v-for="item in 5" :key="item" :value="item * 50">{{ item * 50 }}</option>
                    </select>
                  </div>
                </div>
                <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
              </div>
              <div class="p-controller__navi">
                <div class="p-controller__navi__competitor">
                  <button
                    type="button"
                    :disabled="photosLoading || currentPage === 1"
                    class="p-controller__navi__prev-competitor"
                    @click="goPrevPage"
                  >
                    <Icon name="arrowLeft" />
                  </button>
                  <button
                    type="button"
                    :disabled="photosLoading || currentPage >= photos.meta.total_pages"
                    class="p-controller__navi__next-competitor"
                    @click="goNextPage"
                  >
                    <Icon name="arrowRight" />
                  </button>
                </div>
              </div>
            </div>
            <!-- end: ページネーション -->
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import DatePicker from '../components/DatePicker'
import Loading from '../components/Loading.vue'
import Icon from '../components/Icon.vue'
import { KkSiteIcon } from '../components/base'
import GuideToSetUpCompetitor from '../components/GuideToSetUpCompetitor'
import { toNumberLocaleString } from '../modules/localeString'
import { KkFilterModal } from '../components/projects'
import Modal from '../components/Modal'
import {addAttributes} from '../modules/filterModalConst'

export default {
  name: 'CompaniesCompetitorsPhotos',
  components: {
    DatePicker,
    Loading,
    Icon,
    KkSiteIcon,
    GuideToSetUpCompetitor,
    KkFilterModal,
    Modal,
  },
  data() {
    const companyUuid = this.$route.params.company_uuid
    return {
      companyUuid,
      currentPage: 1,
      sortKey: null,
      sortOrder: null,
      datePickerOpened: false,
      modal: false,
      photoModal: false,
      photoActive: null,
      filterItems: []
    }
  },
  computed: {
    ...mapState({
      photos: state => state.companiesCompetitorsPhotos.data,
      photosLoading: state => state.companiesCompetitorsPhotos.loading,
      formValues: state => state.companiesCompetitorsPhotos.formValues,
      filteredCount: state => state.companiesCompetitorsPhotos.filteredCount,
      searchAttributesLoading: state => state.companiesSearchTagGroups.loading,
      searchAttributes: state => state.companiesSearchTagGroups.data,
      displayNumber: state => state.filters.displayNumber,
      period: state => state.filters.period,
    }),
    ...mapGetters({
      hasReadableCompetitors: 'companiesCompany/hasReadableCompetitors',
      showMoreThanEditorAuthority: 'companiesAccessibleApi/showMoreThanEditorAuthority',
    }),
    loading() {
      return this.photosLoading
    },
    startDataNumber() {
      return (this.currentPage - 1) * this.displayNumber + 1
    },
    endDataNumber() {
      return this.startDataNumber + this.photos.data.length - 1
    },
    readCompetitorsPhotosParams() {
      return {
        company_uuid: this.companyUuid,
        values: {
          biz_companies_photo_form: {
            page: this.currentPage,
            per: Number(this.displayNumber),
            posted_at_term: this.period,
            store_uuids: this.formValues.stores,
            brand_ids: this.formValues.brands,
            competitor_ids: this.formValues.competitors,
            competitor_brand_ids: this.formValues.competitor_brands,
            franchisee_ids: this.formValues.franchisees,
            user_company_ids: this.formValues.user_companies,
            area_ids: this.formValues.areas,
            prefectures: this.formValues.prefectures,
            site_types: this.formValues.sites,
            original_tag_group_a_tag_ids: this.formValues.original_tag_group_a,
            original_tag_group_b_tag_ids: this.formValues.original_tag_group_b,
            original_tag_group_c_tag_ids: this.formValues.original_tag_group_c,
            original_tag_group_d_tag_ids: this.formValues.original_tag_group_d,
            original_tag_group_e_tag_ids: this.formValues.original_tag_group_e,
            sort_key: this.sortKey,
            sort_order: this.sortOrder,
          }
        },
      }
    }
  },
  async created() {
    await this.readSearchAttributes()
    this.initData()
    this.handleSubmit()
  },
  methods: {
    ...mapMutations({
      setFormValues: 'companiesCompetitorsPhotos/setFormValues',
    }),
    ...mapActions({
      readCompetitorsPhotos: 'companiesCompetitorsPhotos/readCompetitorsPhotos',
      readCompetitorsPhotosForFilter: 'companiesCompetitorsPhotos/readCompetitorsPhotosForFilter',
      readSearchAttributes: 'companiesSearchTagGroups/readSearchTagGroups',
      setDisplayNumber: 'filters/setDisplayNumber',
      setPeriod: 'filters/setPeriod',
    }),
    /**
     * 表示件数の変更
     * @param  { Object } e
     * @return { Void }
     */
    changeDisplayNumber(e) {
      this.setDisplayNumber(e.target.value)
      this.handleResetPageAndSubmit()
    },
    /**
     * 期間の変更
     * @param  { Object } value
     * @return { Void }
     */
    changePeriod(value) {
      this.setPeriod(value)
      this.handleResetPageAndSubmit()
    },
    /**
     * 前の写真へ
     * @return { Void }
     */
    prevPhoto() {
      if (this.photoActive === 0) {
        this.photoActive = this.photos.data.length - 1
      } else {
        --this.photoActive
      }
    },
    /**
     * 次の写真へ
     * @return { Void }
     */
    nextPhoto() {
      if (this.photoActive === this.photos.data.length - 1) {
        this.photoActive = 0
      } else {
        ++this.photoActive
      }
    },
    /**
     * 検索条件の初期化
     * @return { Void }
     */
    initData() {
      this.setFormValues(this.formInitialValues())
      this.createFilter()
      this.currentPage = 1
      this.sortKey = null
      this.sortOrder = null
    },
    /**
     * 絞り込み条件モーダルの表示
     * @return { Void }
     */
    openModal() {
      this.modal = true
    },
    /**
     * 絞り込み条件モーダルの非表示
     * @return { Void }
     */
    closeModal() {
      this.modal = false
    },
    /**
     * 写真拡大モーダルの表示
     * @param  { Number} id
     * @return { Void }
     */
    openPhotoModal(id) {
      this.photoActive = id
      this.photoModal = true
    },
    /**
     * 写真拡大モーダルの非表示
     * @return { Void }
     */
    closePhotoModal() {
      this.photoModal = false
    },
    /**
     * 絞り込み条件の初期値を返す
     * @return {{competitors: *, brands: *, competitor_brands: *, stores: *, sites: *, franchisees: *, user_companies: *, areas: *, prefectures: *}}
     */
    formInitialValues() {
      return {
        stores: [],
        brands: [],
        competitors: [],
        competitor_brands: [],
        sites: [],
        franchisees: [],
        user_companies: [],
        areas: [],
        prefectures: [],
        original_tag_group_a: [],
        original_tag_group_b: [],
        original_tag_group_c: [],
        original_tag_group_d: [],
        original_tag_group_e: []
      }
    },
    /**
     * ソート
     * @param  { Object } key
     * @return { Void }
     */
    sort(key) {
      if (this.sortKey === key) {
        this.sortOrder = (this.sortOrder === 'desc' ) ? 'asc' : 'desc'
      } else {
        this.sortKey = key
        this.sortOrder = 'desc'
      }
      this.handleResetPageAndSubmit()
    },
    /**
     * 絞り込みのリセット
     * @return { Void }
     */
    reset() {
      this.setFormValues(this.formInitialValues())
      this.handleSubmit()
    },
    /**
     * 前のページへ
     * @return { Void }
     */
    goPrevPage() {
      --this.currentPage
      this.handleScrollTopAndSubmit()
    },
    /**
     * 次のページへ
     * @return { Void }
     */
    goNextPage() {
      ++this.currentPage
      this.handleScrollTopAndSubmit()
    },
    /**
     * 画面先頭へ移動しデータ更新
     * @return { Void }
     */
    handleScrollTopAndSubmit() {
      window.scrollTo({ top: 0 })
      this.handleSubmit()
    },
    /**
     * 検索条件の変更
     * @return { Void }
     */
    handleResetPageAndSubmit() {
      this.currentPage = 1
      this.handleSubmit()
    },
    /**
     * 検索の実行
     * @return { Void }
     */
    handleSubmit() {
      this.readCompetitorsPhotos(this.readCompetitorsPhotosParams)
      this.datePickerOpened = false
      this.closeModal()
    },
    /**
     * 写真付随テキスト
     * @param  { String } siteInterfaceName
     * @param  { String } siteName
     * @return { Void }
     */
    imageUrlText(siteInterfaceName, siteName) {
      return {
        'google': '問題を報告する',
        'yahoo_loco': '問題を報告する',
        'trip_advisor': `${ siteName }で確認する`
      }[siteInterfaceName]
    },
    /**
     * 数値の3桁区切り
     * @param  { Number } value
     * @return { Number }
     */
    numberLocaleString(value) {
      return toNumberLocaleString(value)
    },
    handleChangeFilterModalCheckBox({ kind, id }) {
      const newFormValues = Object.assign({}, this.formValues)

      if(newFormValues[kind].includes(id)) {
        newFormValues[kind] = newFormValues[kind].filter((_id) => _id !== id)
      } else {
        newFormValues[kind].push(id)
      }

      this.setFormValues(newFormValues)
      this.readCompetitorsPhotosForFilter(this.readCompetitorsPhotosParams)
    },
    createFilter() {
      const attributes = [
        this.searchAttributes.sites,
        this.searchAttributes.brands,
        this.searchAttributes.areas,
        this.searchAttributes.franchisees,
        this.searchAttributes.user_companies,
        this.searchAttributes.competitor_brands
      ]


      if(this.searchAttributes.original_tag_group_a) attributes.push(this.searchAttributes.original_tag_group_a)
      if(this.searchAttributes.original_tag_group_b) attributes.push(this.searchAttributes.original_tag_group_b)
      if(this.searchAttributes.original_tag_group_c) attributes.push(this.searchAttributes.original_tag_group_c)
      if(this.searchAttributes.original_tag_group_d) attributes.push(this.searchAttributes.original_tag_group_d)
      if(this.searchAttributes.original_tag_group_e) attributes.push(this.searchAttributes.original_tag_group_e)

      attributes.push(this.searchAttributes.prefectures)
      attributes.push(this.searchAttributes.stores)
      attributes.push(this.searchAttributes.competitors)

      this.filterItems = attributes.map((attr) => {
        if(attr && attr.kind){
          return addAttributes(attr, attr.kind)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../stylesheets/biz_spa/foundation/_var.scss";

.noData {
  color: $color-base-a
}
.p-photos__site__icon {
  background-color: white;
}
</style>

